import { trace, context, propagation, metrics } from '@opentelemetry/api';
import { WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import { BatchSpanProcessor, ParentBasedSampler, TraceIdRatioBasedSampler } from '@opentelemetry/sdk-trace-base';
import { MeterProvider, PeriodicExportingMetricReader } from '@opentelemetry/sdk-metrics';
import { Resource } from '@opentelemetry/resources';
import { SemanticResourceAttributes } from '@opentelemetry/semantic-conventions';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { OTLPMetricExporter } from '@opentelemetry/exporter-metrics-otlp-http';
import { ZoneContextManager } from '@opentelemetry/context-zone';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { DocumentLoadInstrumentation } from '@opentelemetry/instrumentation-document-load';
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';
import { XMLHttpRequestInstrumentation } from '@opentelemetry/instrumentation-xml-http-request';
import { UserInteractionInstrumentation } from '@opentelemetry/instrumentation-user-interaction';
import { CompositePropagator, W3CTraceContextPropagator, W3CBaggagePropagator } from '@opentelemetry/core';
import { getConfig } from 'config/runtime';

const TRACES_PATH = '/v1/traces';
const METRICS_PATH = '/v1/metrics';

const parseBool = (value, fallback = false) => {
  if (value === undefined || value === null || value === '') return fallback;
  return /^(1|true|yes|on)$/i.test(String(value).trim());
};

const parseRatio = (value, fallback = 1.0) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0 || n > 1) return fallback;
  return n;
};

const buildOtlpUrl = (endpoint, path) => {
  const base = endpoint.replace(/\/+$/, '');
  return base.endsWith(path) ? base : `${base}${path}`;
};

const buildPropagateUrls = (apiBaseUrl) => {
  const urls = [];
  if (apiBaseUrl) {
    try {
      const { origin } = new URL(apiBaseUrl);
      const escaped = origin.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      urls.push(new RegExp(`^${escaped}`));
    } catch {
      // ignore invalid URL
    }
  }
  urls.push(/^https:\/\/([a-z0-9-]+\.)?getandride\.(com|dev)/);
  return urls;
};

let initialized = false;

export function initOpenTelemetry() {
  if (initialized) return true;

  const enabled = parseBool(getConfig('REACT_APP_OTEL_ENABLED'), false);
  const newRelicEnabled = parseBool('REACT_APP_NEW_RELIC_ENABLED', false);

  if (!enabled || newRelicEnabled) return false;

  const endpoint = getConfig('REACT_APP_OTEL_EXPORTER_OTLP_ENDPOINT') || 'https://otel.getandride.dev';
  const serviceName = getConfig('REACT_APP_OTEL_SERVICE_NAME') || 'getandride-backoffice';
  const serviceVersion = getConfig('REACT_APP_OTEL_SERVICE_VERSION') || getConfig('REACT_APP_VERSION') || '0.0.0';
  const environment = getConfig('REACT_APP_OTEL_ENVIRONMENT') || process.env.NODE_ENV || 'production';
  const sampleRatio = parseRatio(getConfig('REACT_APP_OTEL_SAMPLE_RATIO'), 1.0);
  const authToken = getConfig('REACT_APP_OTEL_AUTH_TOKEN') || '';
  const apiBaseUrl = getConfig('REACT_APP_URL_API');

  const resource = new Resource({
    [SemanticResourceAttributes.SERVICE_NAME]: serviceName,
    [SemanticResourceAttributes.SERVICE_VERSION]: serviceVersion,
    [SemanticResourceAttributes.DEPLOYMENT_ENVIRONMENT]: environment,
    [SemanticResourceAttributes.TELEMETRY_SDK_LANGUAGE]: 'webjs',
  });

  const exporterHeaders = authToken ? { Authorization: `Bearer ${authToken}` } : {};

  const exporter = new OTLPTraceExporter({
    url: buildOtlpUrl(endpoint, TRACES_PATH),
    headers: exporterHeaders,
  });

  const provider = new WebTracerProvider({
    resource,
    sampler: new ParentBasedSampler({
      root: new TraceIdRatioBasedSampler(sampleRatio),
    }),
  });

  provider.addSpanProcessor(
    new BatchSpanProcessor(exporter, {
      maxQueueSize: 2048,
      maxExportBatchSize: 512,
      scheduledDelayMillis: 5000,
      exportTimeoutMillis: 4000,
    }),
  );

  provider.register({
    contextManager: new ZoneContextManager(),
    propagator: new CompositePropagator({
      propagators: [new W3CTraceContextPropagator(), new W3CBaggagePropagator()],
    }),
  });

  const propagateTraceHeaderCorsUrls = buildPropagateUrls(apiBaseUrl);

  registerInstrumentations({
    instrumentations: [
      new DocumentLoadInstrumentation(),
      new UserInteractionInstrumentation({
        eventNames: ['click', 'submit'],
      }),
      new XMLHttpRequestInstrumentation({
        propagateTraceHeaderCorsUrls,
        clearTimingResources: true,
      }),
      new FetchInstrumentation({
        propagateTraceHeaderCorsUrls,
        clearTimingResources: true,
      }),
    ],
  });

  const metricExporter = new OTLPMetricExporter({
    url: buildOtlpUrl(endpoint, METRICS_PATH),
    headers: exporterHeaders,
  });

  const meterProvider = new MeterProvider({
    resource,
    readers: [
      new PeriodicExportingMetricReader({
        exporter: metricExporter,
        exportIntervalMillis: 30000,
        exportTimeoutMillis: 10000,
      }),
    ],
  });

  metrics.setGlobalMeterProvider(meterProvider);

  if (typeof window !== 'undefined') {
    window.addEventListener('pagehide', () => {
      meterProvider.forceFlush().catch(() => {});
    });
  }

  initialized = true;
  return true;
}

export function getTracer(name = 'getandride-backoffice') {
  return trace.getTracer(name);
}

export function getMeter(name = 'getandride-backoffice') {
  return metrics.getMeter(name);
}

export { trace, context, propagation, metrics };
